(() => {
  'use strict';

  const parseRatingPayload = (script) => {
    if (!script) return null;
    try {
      let value = JSON.parse(script.textContent || 'null');
      if (typeof value === 'string') value = JSON.parse(value);
      return value && typeof value === 'object' ? value : null;
    } catch (_) {
      return null;
    }
  };

  const trNumber = (value, digits = 1) => Number(value).toLocaleString('tr-TR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

  const ratingPercent = (rating) => `${Math.max(0, Math.min(100, Number(rating) / 5 * 100))}%`;

  const initRatingBadges = (root = document) => {
    root.querySelectorAll('[data-cotton-trustoo-rating]:not([data-rating-ready])').forEach((badge) => {
      const payload = parseRatingPayload(badge.querySelector('[data-cotton-rating-json]'));
      const count = Number(payload?.total_reviews || 0);
      const rating = Number(payload?.rating || 0);
      if (!count || !rating) {
        badge.hidden = true;
        return;
      }
      badge.dataset.ratingReady = 'true';
      const percent = ratingPercent(rating);
      badge.style.setProperty('--rating-percent', percent);
      const stars = badge.querySelector('.cotton-trustoo-rating__stars');
      if (stars) stars.style.setProperty('--rating-percent', percent);
      const text = badge.querySelector('[data-cotton-rating-text]');
      if (text) text.textContent = `${trNumber(rating)} · ${count} yorum`;
      badge.setAttribute('aria-label', `${trNumber(rating)} üzerinden 5, ${count} müşteri yorumu`);
    });
  };

  const make = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  };

  const cloneThemeIcon = (name) => {
    const template = document.querySelector(`template[data-cotton-theme-icon="${name}"]`);
    return template?.content?.firstElementChild?.cloneNode(true) || null;
  };

  const renderDistribution = (container, payload) => {
    if (!container || !payload) return;
    const total = Number(payload.total_reviews || 0);
    container.textContent = '';
    for (let star = 5; star >= 1; star -= 1) {
      const count = Number(payload[`total_star${star}`] || 0);
      const row = make('div', 'cotton-product-reviews__distribution-row');
      row.append(make('span', '', `${star} yıldız`));
      const track = make('span', 'cotton-product-reviews__distribution-track');
      const fill = make('span', 'cotton-product-reviews__distribution-fill');
      fill.style.width = `${total ? count / total * 100 : 0}%`;
      track.append(fill);
      row.append(track, make('span', '', String(count)));
      container.append(row);
    }
  };

  const formatDate = (raw) => {
    const date = new Date(String(raw || '').replace(' ', 'T') + 'Z');
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('tr-TR', { year: 'numeric', month: 'long' }).format(date);
  };

  const renderReviewCard = (review) => {
    const card = make('article', 'cotton-product-review-card');
    const top = make('div', 'cotton-product-review-card__top');
    const stars = make('span', 'cotton-product-review-card__stars', '★★★★★');
    stars.style.setProperty('--rating-percent', ratingPercent(review.star || 0));
    stars.setAttribute('aria-label', `${Number(review.star || 0)} yıldız`);
    top.append(stars);
    const date = formatDate(review.commented_at);
    if (date) top.append(make('time', '', date));

    const author = make('div', 'cotton-product-review-card__author');
    author.append(make('strong', '', String(review.author || 'Cotton müşterisi').trim()));
    if (Number(review.verified_badge) === 1) author.append(make('span', '', 'Doğrulanmış satın alma'));

    card.append(top, author);
    if (review.item_type) card.append(make('p', 'cotton-product-review-card__variant', `Ölçü: ${review.item_type}`));
    if (review.title) card.append(make('h3', '', review.title));
    card.append(make('p', 'cotton-product-review-card__content', review.content || ''));

    const resources = Array.isArray(review.resources) ? review.resources : [];
    const media = resources.map((item) => {
      if (typeof item === 'string') return item;
      return item?.url || item?.src || item?.resource_url || item?.thumb_url || '';
    }).filter((url) => /^https?:\/\//i.test(url));
    if (media.length) {
      const gallery = make('div', 'cotton-product-review-card__media');
      media.slice(0, 4).forEach((url) => {
        const img = make('img');
        img.src = url;
        img.alt = 'Müşteri tarafından paylaşılan ürün fotoğrafı';
        img.loading = 'lazy';
        gallery.append(img);
      });
      card.append(gallery);
    }
    return card;
  };

  const initFullReviews = (section) => {
    const payload = parseRatingPayload(section.querySelector('[data-cotton-review-summary]'));
    const count = Number(payload?.total_reviews || 0);
    const rating = Number(payload?.rating || 0);
    if (!count) {
      section.hidden = true;
      return;
    }

    const score = section.querySelector('[data-cotton-review-score]');
    const countEl = section.querySelector('[data-cotton-review-count]');
    const scoreStars = section.querySelector('.cotton-product-reviews__score-stars');
    if (score) score.textContent = trNumber(rating);
    if (countEl) countEl.textContent = `${count} müşteri yorumu`;
    if (scoreStars) scoreStars.style.setProperty('--rating-percent', ratingPercent(rating));
    renderDistribution(section.querySelector('[data-cotton-review-distribution]'), payload);

    let loaded = false;
    const load = async () => {
      if (loaded) return;
      loaded = true;
      const status = section.querySelector('[data-cotton-review-status]');
      const list = section.querySelector('[data-cotton-review-list]');
      const nav = section.querySelector('[data-cotton-review-nav]');
      const prev = section.querySelector('[data-cotton-review-prev]');
      const next = section.querySelector('[data-cotton-review-next]');
      const page = section.querySelector('[data-cotton-review-page]');
      const productId = section.dataset.productId;
      const shopId = section.dataset.shopId;
      try {
        const reviews = [];
        const pageSize = 20;
        const maxPages = Math.max(1, Math.min(10, Math.ceil(count / pageSize)));
        for (let currentPage = 1; currentPage <= maxPages; currentPage += 1) {
          const url = `https://api.trustoo.io/api/v1/reviews/get_product_reviews?shop_id=${encodeURIComponent(shopId)}&limit=${pageSize}&page=${currentPage}&product_id=${encodeURIComponent(productId)}&sort_by=comprehensive-descending&t=${Date.now()}`;
          const response = await fetch(url, { credentials: 'omit', mode: 'cors' });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const json = await response.json();
          const batch = Array.isArray(json?.data?.list) ? json.data.list : [];
          reviews.push(...batch);
          if (batch.length < pageSize || reviews.length >= count) break;
        }
        if (!reviews.length) throw new Error('empty reviews');

        list.textContent = '';
        reviews.forEach((review) => list.append(renderReviewCard(review)));
        list.hidden = false;
        status.hidden = true;

        const cards = [...list.children];
        const currentIndex = () => cards.reduce((best, card, index) => {
          const distance = Math.abs(card.offsetLeft - list.scrollLeft);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
        const updateNav = () => {
          const index = currentIndex();
          if (page) page.textContent = `${index + 1} / ${cards.length}`;
          if (prev) prev.disabled = index === 0;
          if (next) next.disabled = index >= cards.length - 1;
        };
        const go = (direction) => {
          const target = Math.max(0, Math.min(cards.length - 1, currentIndex() + direction));
          list.scrollTo({ left: cards[target].offsetLeft, behavior: 'smooth' });
        };
        prev?.addEventListener('click', () => go(-1));
        next?.addEventListener('click', () => go(1));
        let scrollFrame = 0;
        list.addEventListener('scroll', () => {
          if (scrollFrame) return;
          scrollFrame = window.requestAnimationFrame(() => {
            scrollFrame = 0;
            updateNav();
          });
        }, { passive: true });
        if (nav && cards.length > 1) nav.hidden = false;
        window.requestAnimationFrame(updateNav);
      } catch (_) {
        status.textContent = 'Yorumlar şu anda yüklenemedi. Lütfen daha sonra tekrar deneyin.';
      }
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      }, { rootMargin: '700px 0px' });
      observer.observe(section);
    } else {
      load();
    }
  };

  const moveCollectionSeo = () => {
    const seo = document.querySelector('[data-cotton-suit-seo]');
    const collectionGrid = document.querySelector('.shopify-section--main-collection');
    if (!seo || !collectionGrid) return;
    collectionGrid.insertAdjacentElement('afterend', seo);
    seo.hidden = false;
  };

  const initStickyVariantControl = () => {
    if (document.documentElement.dataset.cottonStickyVariantReady === 'true') return;
    document.documentElement.dataset.cottonStickyVariantReady = 'true';

    document.addEventListener('change', (event) => {
      const select = event.target.closest('[data-cotton-sticky-variant]');
      if (!select) return;

      const bar = select.closest('[data-cotton-sticky-purchase]');
      const option = select.selectedOptions[0];
      const formId = bar?.dataset.formId || '';
      const optionTitle = String(option?.dataset.optionTitle || '').trim();
      const price = bar?.querySelector('[data-cotton-sticky-price]');
      if (price && option?.dataset.price) price.textContent = formatPanelMoney(option.dataset.price);

      const radio = [...document.querySelectorAll('input[type="radio"]')].find((input) => {
        const label = String(input.labels?.[0]?.textContent || '').trim();
        return input.name.startsWith(`${formId}-`) && label === optionTitle;
      });

      if (radio && !radio.checked) radio.click();
    });
  };

  let activeSizeSheet = null;

  const closeSizeSheet = (restoreFocus = true) => {
    if (!activeSizeSheet) return;
    const { backdrop, trigger, keyHandler } = activeSizeSheet;
    document.removeEventListener('keydown', keyHandler);
    backdrop.classList.remove('is-open');
    document.documentElement.classList.remove('cotton-size-sheet-open');
    trigger.setAttribute('aria-expanded', 'false');
    window.setTimeout(() => backdrop.remove(), 180);
    if (restoreFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
    activeSizeSheet = null;
  };

  const formatPanelMoney = (raw) => {
    const source = String(raw || '').trim();
    const compact = source.replace(/[^\d,.-]/g, '');
    if (!compact) return source;

    const comma = compact.lastIndexOf(',');
    const dot = compact.lastIndexOf('.');
    let normalized = compact;
    if (comma >= 0 && dot >= 0) {
      normalized = dot > comma
        ? compact.replace(/,/g, '')
        : compact.replace(/\./g, '').replace(',', '.');
    } else if (comma >= 0) {
      normalized = compact.length - comma - 1 === 2 ? compact.replace(',', '.') : compact.replace(/,/g, '');
    }

    const value = Number(normalized);
    if (!Number.isFinite(value)) return source;
    return `${value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;
  };

  const normalizeSuitPrices = () => {
    const roots = [...new Set([
      ...document.querySelectorAll('.product-info__block-item'),
      ...document.querySelectorAll('[data-cotton-sticky-price]'),
      ...document.querySelectorAll('cart-drawer, .cart-drawer, [data-cart-drawer]'),
    ])];
    roots.forEach((root) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const nextValue = node.nodeValue.replace(/\d{1,3}(?:,\d{3})*\.\d{2}TL/g, (value) => formatPanelMoney(value));
        if (nextValue !== node.nodeValue) node.nodeValue = nextValue;
      });
    });
    document.querySelectorAll('.product-info__block-item[data-block-type="price"] on-sale-badge')
      .forEach((badge) => {
        if (badge.textContent.trim() !== '%10 indirim') badge.textContent = '%10 indirim';
      });
  };

  const initCartPriceFormatWatch = () => {
    const drawer = document.querySelector('cart-drawer, .cart-drawer, [data-cart-drawer]');
    if (!drawer || drawer.dataset.cottonPriceWatch === 'true') return;
    drawer.dataset.cottonPriceWatch = 'true';
    let frame = 0;
    new MutationObserver((records) => {
      // Ignore our own text formatting and batch genuine inserted/removed elements.
      if (frame || !records.some((record) => [...record.addedNodes, ...record.removedNodes]
        .some((node) => node.nodeType === 1))) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        normalizeSuitPrices();
      });
    }).observe(drawer, { childList: true, subtree: true });
  };

  const openSizeSheet = (select, trigger) => {
    closeSizeSheet(false);

    const backdrop = make('div', 'cotton-suit-size-sheet');
    const panel = make('section', 'cotton-suit-size-sheet__panel');
    const panelId = `cotton-size-sheet-${Date.now()}`;
    panel.id = panelId;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', `${panelId}-title`);

    const dragHandle = make('span', 'cotton-suit-size-sheet__drag-handle');
    dragHandle.setAttribute('aria-hidden', 'true');
    const header = make('div', 'cotton-suit-size-sheet__header');
    const heading = make('div');
    const eyebrow = make('span', '', 'Suit ölçüleri');
    const title = make('strong', '', 'Ebat seçin');
    title.id = `${panelId}-title`;
    heading.append(eyebrow, title);
    const close = make('button', 'cotton-suit-size-sheet__close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Ebat seçimini kapat');
    const closeIcon = cloneThemeIcon('close');
    if (closeIcon) close.append(closeIcon);
    header.append(heading, close);

    const list = make('div', 'cotton-suit-size-sheet__list');
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Halı ölçüleri');
    let selectedRow = null;

    [...select.options].forEach((option) => {
      const row = make('button', 'cotton-suit-size-sheet__option');
      row.type = 'button';
      row.setAttribute('role', 'option');
      row.setAttribute('aria-selected', String(option.selected));
      row.disabled = option.disabled;
      if (option.selected) {
        row.classList.add('is-selected');
        selectedRow = row;
      }

      const copy = make('span', 'cotton-suit-size-sheet__option-copy');
      copy.append(
        make('strong', '', option.dataset.optionTitle || option.textContent.trim()),
        make('span', '', option.disabled ? 'Tükendi' : formatPanelMoney(option.dataset.price || '')),
      );
      const check = make('span', 'cotton-suit-size-sheet__check');
      check.setAttribute('aria-hidden', 'true');
      if (option.selected) {
        const successIcon = cloneThemeIcon('success');
        if (successIcon) check.append(successIcon);
      }
      row.append(copy, check);

      row.addEventListener('click', () => {
        if (option.disabled) return;
        select.value = option.value;
        const text = trigger.querySelector('[data-cotton-size-trigger-text]');
        if (text) text.textContent = option.dataset.optionTitle || option.textContent.trim();
        closeSizeSheet(false);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
      list.append(row);
    });

    panel.append(dragHandle, header, list);
    backdrop.append(panel);
    backdrop.addEventListener('click', (event) => {
      if (event.target === backdrop) closeSizeSheet();
    });
    close.addEventListener('click', () => closeSizeSheet());

    let gestureStartX = 0;
    let gestureStartY = 0;
    let gestureStartedAt = 0;
    let dragDistance = 0;
    let dragging = false;
    const resetPanelPosition = () => {
      panel.style.transition = 'transform .18s ease';
      panel.style.transform = 'translateY(0)';
      backdrop.style.removeProperty('background-color');
      window.setTimeout(() => {
        if (!panel.isConnected) return;
        panel.style.removeProperty('transition');
        panel.style.removeProperty('transform');
      }, 190);
    };
    panel.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      gestureStartX = touch.clientX;
      gestureStartY = touch.clientY;
      gestureStartedAt = performance.now();
      dragDistance = 0;
      dragging = false;
    }, { passive: true });
    panel.addEventListener('touchmove', (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (list.scrollTop > 0) {
        gestureStartX = touch.clientX;
        gestureStartY = touch.clientY;
        gestureStartedAt = performance.now();
        return;
      }
      const deltaX = touch.clientX - gestureStartX;
      const deltaY = touch.clientY - gestureStartY;
      if (deltaY <= 0 || Math.abs(deltaX) > deltaY) return;
      dragging = true;
      dragDistance = Math.min(deltaY, window.innerHeight * .6);
      panel.style.transition = 'none';
      panel.style.transform = `translateY(${dragDistance}px)`;
      const shade = Math.max(.08, .46 * (1 - dragDistance / (window.innerHeight * .6)));
      backdrop.style.backgroundColor = `rgba(11, 22, 14, ${shade})`;
      if (event.cancelable) event.preventDefault();
    }, { passive: false });
    const finishDrag = () => {
      if (!dragging) return;
      const elapsed = Math.max(1, performance.now() - gestureStartedAt);
      const velocity = dragDistance / elapsed;
      dragging = false;
      if (dragDistance >= 90 || velocity >= .55) {
        panel.style.transition = 'transform .18s ease';
        panel.style.transform = 'translateY(100%)';
        closeSizeSheet();
        return;
      }
      resetPanelPosition();
    };
    panel.addEventListener('touchend', finishDrag, { passive: true });
    panel.addEventListener('touchcancel', finishDrag, { passive: true });

    const keyHandler = (event) => {
      if (event.key === 'Escape') closeSizeSheet();
    };
    activeSizeSheet = { backdrop, trigger, keyHandler };
    document.addEventListener('keydown', keyHandler);
    document.documentElement.classList.add('cotton-size-sheet-open');
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('aria-controls', panelId);
    document.body.append(backdrop);
    window.requestAnimationFrame(() => {
      backdrop.classList.add('is-open');
      (selectedRow || close).focus({ preventScroll: true });
      selectedRow?.scrollIntoView({ block: 'nearest' });
    });
  };

  const enhanceVariantSelects = (root = document) => {
    root.querySelectorAll('[data-cotton-sticky-variant]:not([data-cotton-size-enhanced])').forEach((select) => {
      select.dataset.cottonSizeEnhanced = 'true';
      select.classList.add('cotton-suit-native-select');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');

      const trigger = make('button', 'cotton-suit-size-trigger');
      trigger.type = 'button';
      trigger.dataset.cottonSizeTrigger = 'true';
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      const text = make('span', '', select.selectedOptions[0]?.dataset.optionTitle || select.selectedOptions[0]?.textContent.trim() || 'Ebat seçin');
      text.dataset.cottonSizeTriggerText = 'true';
      const chevron = make('span', 'cotton-suit-size-trigger__chevron');
      chevron.setAttribute('aria-hidden', 'true');
      const chevronIcon = cloneThemeIcon('chevron-bottom');
      if (chevronIcon) chevron.append(chevronIcon);
      trigger.append(text, chevron);
      trigger.addEventListener('click', () => openSizeSheet(select, trigger));
      select.insertAdjacentElement('afterend', trigger);
    });
  };

  const updateStickyPurchaseVisibility = () => {
    const quickAdd = document.querySelector('product-quick-add:has([data-cotton-sticky-purchase])');
    const mainBuy = document.querySelector('.product-info__block-item[data-block-type="buy-buttons"]');
    const footer = document.querySelector('.shopify-section--footer');
    if (!quickAdd || !mainBuy) return;

    const passedMainBuy = mainBuy.getBoundingClientRect().bottom < 0;
    const footerIsClear = !footer || footer.getBoundingClientRect().top > window.innerHeight - 90;
    quickAdd.classList.toggle('cotton-suit-quick-visible', passedMainBuy && footerIsClear);
  };

  const initStickyPurchaseVisibility = () => {
    if (document.documentElement.dataset.cottonStickyVisibilityReady === 'true') return;
    document.documentElement.dataset.cottonStickyVisibilityReady = 'true';
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateStickyPurchaseVisibility();
      });
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    schedule();
  };

  const buildMobileMainVariantSelect = () => {
    if (!window.matchMedia('(max-width: 699px)').matches) return;
    const source = document.querySelector('[data-cotton-main-variant-source]');
    const block = document.querySelector('.product-info__block-item[data-block-type="variant-picker"]');
    if (!source || !block || block.querySelector('[data-cotton-main-size]')) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'cotton-suit-main-size-select';
    wrapper.dataset.cottonMainSize = 'true';
    wrapper.dataset.cottonStickyPurchase = 'true';
    wrapper.dataset.formId = source.dataset.formId || '';

    const label = document.createElement('span');
    label.textContent = 'Ebat seçin';
    const select = source.cloneNode(true);
    select.hidden = false;
    select.removeAttribute('hidden');
    select.removeAttribute('aria-hidden');
    select.removeAttribute('tabindex');
    select.removeAttribute('data-cotton-main-variant-source');
    select.setAttribute('data-cotton-sticky-variant', 'true');
    select.setAttribute('aria-label', 'Halı ölçüsü seçin');
    wrapper.append(label, select);
    block.prepend(wrapper);
    block.classList.add('cotton-suit-main-size-ready');
  };

  const initVariantRerenderWatch = () => {
    if (document.documentElement.dataset.cottonVariantWatch === 'event-v2') return;
    document.documentElement.dataset.cottonVariantWatch = 'event-v2';
    let frame = 0;
    // Impact already emits this event when a real variant response is ready.
    // Listen in capture so replacement of a form/root cannot lose the hook.
    // Unlike the old subtree observers, our own price/text writes do not retrigger it.
    document.addEventListener('product:rerender', () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        buildMobileMainVariantSelect();
        enhanceVariantSelects();
        normalizeSuitPrices();
        updateStickyPurchaseVisibility();
      });
    }, true);
  };

  const optimizeMobilePdpOrder = () => {
    if (!window.matchMedia('(max-width: 699px)').matches) return;
    const benefits = document.querySelector('.cotton-suit-pdp-pilot');
    const buyBlock = document.querySelector('.product-info__block-item[data-block-type="buy-buttons"]');
    if (!benefits || !buyBlock || benefits.previousElementSibling === buyBlock) return;
    buyBlock.insertAdjacentElement('afterend', benefits);
  };

  const init = () => {
    initRatingBadges();
    moveCollectionSeo();
    initStickyVariantControl();
    initStickyPurchaseVisibility();
    buildMobileMainVariantSelect();
    enhanceVariantSelects();
    normalizeSuitPrices();
    initCartPriceFormatWatch();
    initVariantRerenderWatch();
    optimizeMobilePdpOrder();
    document.querySelectorAll('[data-cotton-reviews]').forEach(initFullReviews);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
